import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { FetchCompanyApplicationsParams } from "../lib/applications-contract";
import {
  fetchCompanyApplications,
  fetchTalentSubmissionDetail,
  isApplicationsNotFoundError,
} from "../lib/applications-api";
import { QUERY_KEYS } from "../lib/query-keys";

/**
 * Data hooks for the company-wide Applications inbox (Employer Phase 1 WP4).
 *
 * Status changes and messaging reuse the existing per-job hooks in
 * `useJobsApi.ts` (`useUpdateEmployerApplicantStatus`,
 * `useSendEmployerApplicantMessage`) — inbox rows carry the jobId they need.
 */

function serializeCompanyApplicationsParams(
  params: FetchCompanyApplicationsParams = {},
) {
  return JSON.stringify({
    query: params.query ?? "",
    destinations: params.destinations ?? [],
    statuses: params.statuses ?? [],
    sort: params.sort ?? "activity",
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
  });
}

/**
 * The aggregate endpoint ships after this frontend: a 404 means "backend not
 * deployed yet", so retrying is pure noise — fail straight into the error
 * state. Everything else keeps the default retry behavior.
 */
function retryUnlessMissing(failureCount: number, error: Error) {
  if (isApplicationsNotFoundError(error)) return false;
  return failureCount < 2;
}

export function useCompanyApplications(
  params: FetchCompanyApplicationsParams = {},
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEYS.COMPANY_APPLICATIONS(
      serializeCompanyApplicationsParams(params),
    ),
    queryFn: () => fetchCompanyApplications(params),
    enabled,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
    retry: retryUnlessMissing,
  });
}

export function useTalentSubmissionDetail(
  submissionId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEYS.TALENT_SUBMISSION_DETAIL(submissionId),
    queryFn: () => fetchTalentSubmissionDetail(submissionId as string),
    enabled: enabled && Boolean(submissionId),
    staleTime: 15_000,
    retry: retryUnlessMissing,
  });
}
